import type { JSONContent } from "@tiptap/core";
import type { Root as MdastRoot } from "mdast";

const INLINE_TYPES = new Set([
  "text",
  "emphasis",
  "strong",
  "delete",
  "inlineCode",
  "link",
  "break",
  "image",
  "html",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function childrenOf(node: Record<string, unknown>): Record<string, unknown>[] {
  return Array.isArray(node.children) ? node.children.filter(isRecord) : [];
}

function textOf(node: Record<string, unknown>): string {
  if (typeof node.value === "string") return node.value;
  return childrenOf(node).map(textOf).join("");
}

function imageDimension(value: unknown): number | undefined {
  const dimension = Number(value);
  return Number.isFinite(dimension) && dimension > 0 ? dimension : undefined;
}

function imageDimensionsFromMdast(
  node: Record<string, unknown>
): Record<string, number> {
  const data = isRecord(node.data) ? node.data : undefined;
  const properties = isRecord(data?.hProperties) ? data.hProperties : undefined;
  const width = imageDimension(properties?.width);
  const height = imageDimension(properties?.height);
  return {
    ...(width !== undefined && { width }),
    ...(height !== undefined && { height }),
  };
}

type TiptapMark = NonNullable<JSONContent["marks"]>[number];

function withMark(content: JSONContent[], mark: TiptapMark): JSONContent[] {
  return content.map((node) => {
    if (node.type === "text") {
      return { ...node, marks: [...(node.marks ?? []), mark] };
    }
    return node.content
      ? { ...node, content: withMark(node.content, mark) }
      : node;
  });
}

function mdastInlineToTiptap(node: Record<string, unknown>): JSONContent[] {
  switch (node.type) {
    case "text":
      return [{ type: "text", text: String(node.value ?? "") }];
    case "strong":
      return withMark(mdastChildrenToTiptap(node), { type: "bold" });
    case "emphasis":
      return withMark(mdastChildrenToTiptap(node), { type: "italic" });
    case "delete":
      return withMark(mdastChildrenToTiptap(node), { type: "strike" });
    case "inlineCode":
      return [
        {
          type: "text",
          text: String(node.value ?? ""),
          marks: [{ type: "code" }],
        },
      ];
    case "break":
      return [{ type: "hardBreak" }];
    case "image":
      return [
        {
          type: "image",
          attrs: {
            src: String(node.url ?? ""),
            alt: typeof node.alt === "string" ? node.alt : null,
            title: typeof node.title === "string" ? node.title : null,
            ...imageDimensionsFromMdast(node),
          },
        },
      ];
    case "link": {
      const data = isRecord(node.data) ? node.data : undefined;
      if (data?.reference !== undefined) {
        return [
          {
            type: "sourceMapLink",
            attrs: {
              label: textOf(node),
              url: typeof node.url === "string" ? node.url : "#",
              reference: data.reference,
            },
          },
        ];
      }
      return withMark(mdastChildrenToTiptap(node), {
        type: "link",
        attrs: {
          href: typeof node.url === "string" ? node.url : "",
          target: null,
          rel: null,
          class: null,
          title: typeof node.title === "string" ? node.title : null,
        },
      });
    }
    default:
      return [
        {
          type: "mdastOpaque",
          attrs: { node, label: textOf(node) || String(node.value ?? "") },
        },
      ];
  }
}

function mdastChildrenToTiptap(node: Record<string, unknown>): JSONContent[] {
  return childrenOf(node).flatMap((child) =>
    INLINE_TYPES.has(String(child.type))
      ? mdastInlineToTiptap(child)
      : [mdastBlockToTiptap(child)]
  );
}

function mdastPhrasingToTiptapBlocks(
  node: Record<string, unknown>
): JSONContent[] {
  const blocks: JSONContent[] = [];
  let inlineContent: JSONContent[] = [];
  const flushInlineContent = () => {
    if (inlineContent.length === 0) return;
    blocks.push({ type: "paragraph", content: inlineContent });
    inlineContent = [];
  };

  for (const child of childrenOf(node)) {
    if (child.type === "image") {
      flushInlineContent();
      blocks.push(...mdastInlineToTiptap(child));
    } else {
      inlineContent.push(...mdastInlineToTiptap(child));
    }
  }
  flushInlineContent();

  return blocks.length > 0 ? blocks : [{ type: "paragraph" }];
}

function mdastBlocksToTiptap(node: Record<string, unknown>): JSONContent[] {
  return node.type === "paragraph"
    ? mdastPhrasingToTiptapBlocks(node)
    : [mdastBlockToTiptap(node)];
}

function tableCellToTiptap(
  node: Record<string, unknown>,
  header: boolean
): JSONContent {
  return {
    type: header ? "tableHeader" : "tableCell",
    content: mdastPhrasingToTiptapBlocks(node),
  };
}

function mdastBlockToTiptap(node: Record<string, unknown>): JSONContent {
  switch (node.type) {
    case "paragraph":
      return { type: "paragraph", content: mdastChildrenToTiptap(node) };
    case "heading":
      return {
        type: "heading",
        attrs: { level: typeof node.depth === "number" ? node.depth : 2 },
        content: mdastChildrenToTiptap(node),
      };
    case "blockquote":
      return {
        type: "blockquote",
        content: childrenOf(node).flatMap(mdastBlocksToTiptap),
      };
    case "list":
      return {
        type: node.ordered ? "orderedList" : "bulletList",
        attrs: node.ordered
          ? { start: node.start ?? 1, type: null }
          : undefined,
        content: childrenOf(node).map(mdastBlockToTiptap),
      };
    case "listItem":
      return {
        type: "listItem",
        content: childrenOf(node).flatMap(mdastBlocksToTiptap),
      };
    case "code":
      return {
        type: "codeBlock",
        attrs: { language: typeof node.lang === "string" ? node.lang : null },
        content: [{ type: "text", text: String(node.value ?? "") }],
      };
    case "thematicBreak":
      return { type: "horizontalRule" };
    case "table": {
      const rows = childrenOf(node);
      return {
        type: "table",
        attrs: { align: Array.isArray(node.align) ? node.align : null },
        content: rows.map((row, rowIndex) => ({
          type: "tableRow",
          content: childrenOf(row).map((cell) =>
            tableCellToTiptap(cell, rowIndex === 0)
          ),
        })),
      };
    }
    default:
      return {
        type: "paragraph",
        content: [
          {
            type: "mdastOpaque",
            attrs: { node, label: textOf(node) || String(node.value ?? "") },
          },
        ],
      };
  }
}

export function reportMdastToTiptap(tree: MdastRoot): JSONContent {
  return {
    type: "doc",
    content: tree.children.flatMap((node) =>
      mdastBlocksToTiptap(node as unknown as Record<string, unknown>)
    ),
  };
}

function applyMarks(
  node: Record<string, unknown>,
  marks: unknown
): Record<string, unknown> {
  if (!Array.isArray(marks)) return node;
  return marks
    .filter(isRecord)
    .reduce<Record<string, unknown>>((current, mark) => {
      switch (mark.type) {
        case "bold":
          return { type: "strong", children: [current] };
        case "italic":
          return { type: "emphasis", children: [current] };
        case "strike":
          return { type: "delete", children: [current] };
        case "code":
          return { type: "inlineCode", value: current.value ?? "" };
        case "link":
          return {
            type: "link",
            url: isRecord(mark.attrs) ? String(mark.attrs.href ?? "") : "",
            title:
              isRecord(mark.attrs) && typeof mark.attrs.title === "string"
                ? mark.attrs.title
                : null,
            children: [current],
          };
        default:
          return current;
      }
    }, node);
}

function tiptapInlineToMdast(node: JSONContent): Record<string, unknown>[] {
  if (node.type === "text") {
    return [applyMarks({ type: "text", value: node.text ?? "" }, node.marks)];
  }
  if (node.type === "hardBreak") return [{ type: "break" }];
  if (node.type === "image") {
    const width = imageDimension(node.attrs?.width);
    const height = imageDimension(node.attrs?.height);
    const hProperties = {
      ...(width !== undefined && { width }),
      ...(height !== undefined && { height }),
    };
    return [
      {
        type: "image",
        url: String(node.attrs?.src ?? ""),
        alt: typeof node.attrs?.alt === "string" ? node.attrs.alt : null,
        title: typeof node.attrs?.title === "string" ? node.attrs.title : null,
        ...(Object.keys(hProperties).length > 0 && {
          data: { hProperties },
        }),
      },
    ];
  }
  if (node.type === "sourceMapLink") {
    return [
      {
        type: "link",
        url: String(node.attrs?.url ?? "#"),
        title: null,
        data: { reference: node.attrs?.reference },
        children: [{ type: "text", value: String(node.attrs?.label ?? "") }],
      },
    ];
  }
  if (node.type === "mdastOpaque" && isRecord(node.attrs?.node)) {
    return [node.attrs.node];
  }
  return (node.content ?? []).flatMap(tiptapInlineToMdast);
}

function paragraphContent(node: JSONContent): Record<string, unknown>[] {
  return (node.content ?? []).flatMap(tiptapInlineToMdast);
}

function tiptapBlockToMdast(node: JSONContent): Record<string, unknown> {
  switch (node.type) {
    case "paragraph":
      return { type: "paragraph", children: paragraphContent(node) };
    case "image":
      return { type: "paragraph", children: tiptapInlineToMdast(node) };
    case "heading":
      return {
        type: "heading",
        depth: Number(node.attrs?.level ?? 2),
        children: paragraphContent(node),
      };
    case "blockquote":
      return {
        type: "blockquote",
        children: (node.content ?? []).map(tiptapBlockToMdast),
      };
    case "bulletList":
      return {
        type: "list",
        ordered: false,
        spread: false,
        children: (node.content ?? []).map(tiptapBlockToMdast),
      };
    case "orderedList":
      return {
        type: "list",
        ordered: true,
        start: Number(node.attrs?.start ?? 1),
        spread: false,
        children: (node.content ?? []).map(tiptapBlockToMdast),
      };
    case "listItem":
      return {
        type: "listItem",
        spread: false,
        checked: null,
        children: (node.content ?? []).map(tiptapBlockToMdast),
      };
    case "codeBlock":
      return {
        type: "code",
        lang:
          typeof node.attrs?.language === "string" ? node.attrs.language : null,
        meta: null,
        value: (node.content ?? []).map((child) => child.text ?? "").join(""),
      };
    case "horizontalRule":
      return { type: "thematicBreak" };
    case "table":
      return {
        type: "table",
        align: Array.isArray(node.attrs?.align) ? node.attrs.align : [],
        children: (node.content ?? []).map((row) => ({
          type: "tableRow",
          children: (row.content ?? []).map((cell) => ({
            type: "tableCell",
            children: (cell.content ?? []).flatMap((block) =>
              block.type === "paragraph"
                ? paragraphContent(block)
                : [tiptapBlockToMdast(block)]
            ),
          })),
        })),
      };
    default:
      if (node.type === "mdastOpaque" && isRecord(node.attrs?.node)) {
        return node.attrs.node;
      }
      return { type: "paragraph", children: paragraphContent(node) };
  }
}

export function tiptapToReportMdast(document: JSONContent): MdastRoot {
  return {
    type: "root",
    children: (document.content ?? []).map(tiptapBlockToMdast),
  } as unknown as MdastRoot;
}
